/* eslint-disable jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import styles from './modal.css';

/**
 * Pure functional modal component
 * Receives all props from a domain specific container, including event handling functions and prepared state
 * Up for debate whether we allow https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md
 * @param {Object} props - The required props
 * @param {Object} props.children - children to render inside the body of the modal
 * @param {Object} props.stateProps - the isOpen state
 * @param {Object} props.contentProps - open and close buttons
 * @param {Object} props.visualProps - modal variant classes
 * @param {Object} props.eventProps - the overlay onClick event handler
 * @returns {Node} the modal component
 */
const Modal = ({ children, stateProps, contentProps, visualProps, eventProps }) => {
    const { isOpen, xs } = stateProps;
    const { openComponent, headerComponents, footerComponents, closeIcon } = contentProps;
    const { onOverlayClick } = eventProps;
    // I elected not to use defaults here to ensure they're properly defined in the components, but you could easily add defaults.
    const { openComponentDisplay, overlayVariant, outerVariant, containerVariant, contentVariant } = visualProps;
    const showHeader = headerComponents && headerComponents.length > 0;
    return (
        <div className={styles[`display_${openComponentDisplay}`]}>
            {openComponent}
            {isOpen
            && (
                <div
                    id="overlay"
                    className={classNames(styles.overlay_base, styles[`overlay_${overlayVariant}`])}
                    onClick={onOverlayClick}
                >
                    <div className={styles[`modal_outer_${outerVariant}`]}>
                        <div className={styles[`modal_container_${containerVariant}`]} data-modal-content="true">
                            {xs && showHeader
                        && (
                            <div className={styles.modal_header_bar}>
                                {headerComponents}
                            </div>
                        )}
                            <div className={classNames(styles.modal_body, styles[`modal_body_${contentVariant}`])}>
                                {children}
                                {footerComponents}
                            </div>
                        </div>
                        {closeIcon}
                    </div>
                </div>
            )}
        </div>
    );
};

Modal.defaultProps = {
    stateProps: {},
    contentProps: {},
    eventProps: {},
    visualProps: {}
};

Modal.propTypes = {
    // The 'modal' used for MoreInfo ends up with children as an object, all other modals have children as an array
    children: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array
    ]),
    stateProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        xs: PropTypes.bool
    }),
    contentProps: PropTypes.shape({
        closeIcon: PropTypes.object,
        openComponent: PropTypes.array,
        headerComponents: PropTypes.array,
        footerComponents: PropTypes.array
    }),
    eventProps: PropTypes.shape({
        onOverlayClick: PropTypes.func.isRequired
    }),
    visualProps: PropTypes.shape({
        contentVariant: PropTypes.string,
        modalOverflow: PropTypes.string,
        openComponentDisplay: PropTypes.oneOf(['inline_block']),
        overlayVariant: PropTypes.oneOf(['dark', 'light']),
        outerVariant: PropTypes.oneOf(['centered', 'full_width_xs_centered_sm', 'centered_large']),
        containerVariant: PropTypes.oneOf(['centered', 'full_width_xs_centered_sm', 'large'])
    })
};

export { Modal };
